/**
 * The termo de liberação para menores, as a document.
 *
 * What the document SAYS is modelled here; the jsPDF calls that draw it live in the component.
 * That split is deliberate: the wording, the ordering and the "is this approved" marking are the
 * parts with legal weight and the parts worth reviewing, and a test can read this — it cannot
 * read a canvas.
 *
 * The layout follows the paper form the condomínio uses today ("AUTORIZAÇÃO COM TERMO DE
 * RESPONSABILIDADE DE PAIS E TUTORES RESPONSÁVEIS PARA LIBERAÇÃO DO USO DAS FACIAIS DE ENTRADA E
 * SAÍDA DO CONDOMÍNIO"), so a printed one can be filed next to the ones already in the cabinet.
 */

export interface MinorRelease {
  id: string;
  person_id: string;
  /** The unit the termo was filed against — the unit pages filter on it. */
  unit_id?: string | null;
  guardian_name: string;
  guardian_rg: string;
  guardian_cpf: string;
  father_name: string;
  father_rg: string;
  father_cpf: string;
  mother_name: string;
  mother_rg: string;
  mother_cpf: string;
  minor_name: string;
  minor_rg: string;
  minor_cpf: string;
  minor_birth: string;
  access_scope: string;
  signature_data: string;
  declaration_text: string;
  signed_by_name: string;
  signed_at: string;
  status: string;
  decided_by_name: string;
  decided_at: string | null;
  decision_reason: string;
}

/** The termo's wording, transcribed from the paper form. */
export const DECLARATION_TEXT = [
  'Pelo presente termo de autorização e responsabilidade, AUTORIZO/AUTORIZAMOS o(s) menor(es) ' +
    'acima indicado(s) a utilizar as faciais do condomínio sem a minha/nossa presença e ' +
    'DECLARO/DECLARAMOS, para todos os fins de direito, que assumo/assumimos todas as ' +
    'responsabilidades civis e criminais e perante o Estatuto da Criança e do Adolescente (E.C.A.).',
  'Declaro/declaramos, ainda, que o condomínio, a Panther Segurança Eletrônica e seus ' +
    'representantes legais estão isentos de quaisquer responsabilidades quanto à integridade ' +
    'física e danos (físicos/morais) que as crianças constantes do presente termo venham a ' +
    'ocasionar a si e/ou a terceiros.',
  'Por ser verdade, firmo/firmamos a presente autorização sob penas da lei e ' +
    'confirmo/confirmamos a veracidade das declarações, estando de acordo com os riscos aos ' +
    'quais estarei/estaremos sujeito(s), fazendo-o de forma voluntária e livre de qualquer ' +
    'vício de consentimento.',
].join('\n\n');

/**
 * The three boxes the paper form has per minor.
 *
 * An unrecognised value is printed as-is rather than dropped: WHAT was authorised is the entire
 * purpose of the document, and a blank there would make the PDF say the guardian authorised
 * nothing at all.
 */
export const accessScopeLabel = (scope: string): string => {
  const key = (scope ?? '').trim();
  if (!key) return 'Não informado';
  return ({
    entrada: 'Liberado entrada',
    saida: 'Liberado saída',
    entrada_saida: 'Liberado entrada/saída',
  } as Record<string, string>)[key] ?? key;
};

export const minorReleaseStatusLabel = (status: string): string =>
  ({
    pendente: 'Aguardando aprovação',
    aprovado: 'Aprovado',
    rejeitado: 'Rejeitado',
  } as Record<string, string>)[(status ?? '').trim()] ?? status;

/** "2014-03-04" → "04/03/2014". Split, never reparsed — see the shift_date note. */
const brDate = (iso: string): string => {
  const parts = (iso ?? '').slice(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : iso ?? '';
};

const brDateTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} às ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export interface DocumentSection {
  heading: string;
  rows: [string, string][];
}

export interface MinorReleaseDocument {
  title: string;
  subtitle: string;
  sections: DocumentSection[];
  declaration: string;
  signature: { signedBy: string; signedOn: string; image: string };
  decision: string;
  /**
   * Stamped across an unapproved print.
   *
   * Without it a pending termo prints identically to an approved one, and a sheet of paper that
   * looks authorised is exactly the thing somebody waves at a porteiro.
   */
  watermark: string | null;
}

export const minorReleaseDocument = (
  r: MinorRelease,
  ctx: { customerName: string; unitLabel?: string },
): MinorReleaseDocument => {
  const sections: DocumentSection[] = [
    {
      heading: 'Representante legal',
      rows: [
        ['Nome', r.guardian_name],
        ['RG', r.guardian_rg],
        ['CPF', r.guardian_cpf],
      ],
    },
  ];

  // A parent nobody filled in is omitted rather than printed as an empty box: the paper form has
  // fixed boxes, but an empty box on a generated document reads like information that was lost.
  if (r.father_name?.trim()) {
    sections.push({
      heading: 'Dados do pai',
      rows: [['Nome', r.father_name], ['RG', r.father_rg], ['CPF', r.father_cpf]],
    });
  }
  if (r.mother_name?.trim()) {
    sections.push({
      heading: 'Dados da mãe',
      rows: [['Nome', r.mother_name], ['RG', r.mother_rg], ['CPF', r.mother_cpf]],
    });
  }

  sections.push({
    heading: 'Dados do menor',
    rows: [
      ['Nome', r.minor_name],
      ['RG', r.minor_rg],
      ['CPF', r.minor_cpf],
      ['Data de nascimento', brDate(r.minor_birth)],
      ['Autorização', accessScopeLabel(r.access_scope)],
    ],
  });

  const status = (r.status ?? '').trim();
  let decision: string;
  if (status === 'aprovado') {
    decision = `Aprovado por ${r.decided_by_name || '—'} em ${brDateTime(r.decided_at)}`.trim();
  } else if (status === 'rejeitado') {
    decision = `Rejeitado por ${r.decided_by_name || '—'} em ${brDateTime(r.decided_at)}` +
      (r.decision_reason?.trim() ? ` — ${r.decision_reason.trim()}` : '');
  } else {
    decision = 'Aguardando aprovação do síndico';
  }

  return {
    title: 'AUTORIZAÇÃO COM TERMO DE RESPONSABILIDADE DE PAIS E TUTORES RESPONSÁVEIS ' +
      'PARA LIBERAÇÃO DO USO DAS FACIAIS DE ENTRADA E SAÍDA DO CONDOMÍNIO',
    subtitle: [ctx.customerName, ctx.unitLabel].filter(Boolean).join(' · '),
    sections,
    // The text AS SIGNED, not today's constant: changing the wording later must not rewrite a
    // document somebody already put their name to.
    declaration: r.declaration_text?.trim() || DECLARATION_TEXT,
    signature: {
      signedBy: r.signed_by_name,
      signedOn: brDateTime(r.signed_at),
      image: r.signature_data,
    },
    decision,
    watermark: status === 'aprovado' ? null : minorReleaseStatusLabel(status).toUpperCase(),
  };
};

/** "termo-menor-joao-souza-2026-10-28.pdf" — a folder of these sorts by child, then date. */
export const minorReleaseFileName = (r: MinorRelease): string => {
  const slug = (r.minor_name ?? '')
    .normalize('NFD')
    // Accents, as combining marks. By escape rather than a literal range: the literal is
    // invisible in a diff and trivially mangled by an editor.
    .replace(/[\u0300-\u036f]/g, '')
    // Apostrophes vanish rather than becoming separators — "D'Ávila" is one word, and
    // "d-avila" reads as two.
    .replace(/['\u2018\u2019]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  const day = (r.signed_at ?? '').slice(0, 10);
  return `termo-menor-${slug || 'sem-nome'}-${day}.pdf`;
};
